import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    LinearProgress,
    Chip,
    TextField,
    Stack,
    Fade,
    Collapse
} from '@mui/material';
import {
    QuestionMark,
    People,
    Schedule,
    RadioButtonUnchecked
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import ApiFetch from './ApiFetch';
import Dialog from './Dialog';
import { useTranslation } from "react-i18next";

const QuestionAnswerFixed = (props) => {
    // カスタムアニメーション定義 - MUI/systemのkeyframesを使用
    const slideInFromButton = keyframes`
        0% {
            transform: scale(0.95);
            opacity: 0.8;
        }
        50% {
            transform: scale(1.02);
            opacity: 0.9;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    `;

    const progressAnimation = keyframes`
        0% {
            width: 0%;
        }
        100% {
            width: var(--progress-width);
        }
    `;

    const hoverPulse = keyframes`
        0% {
            box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
        }
        50% {
            box-shadow: 0 8px 20px rgba(33, 150, 243, 0.25);
        }
        100% {
            box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
        }
    `;

    const subtleGlow = keyframes`
        0% {
            background-position: 0% 0%;
        }
        100% {
            background-position: 100% 100%;
        }
    `;

    const fadeInScale = keyframes`
        0% {
            opacity: 0;
            transform: scale(0.8);
        }
        100% {
            opacity: 1;
            transform: scale(1);
        }
    `;

    const slideInFromLeft = keyframes`
        0% {
            opacity: 0;
            transform: translateX(-30px);
        }
        100% {
            opacity: 1;
            transform: translateX(0);
        }
    `;

    // State variables
    const [mode, setMode] = useState(props.mode || "");
    const [answerdCount, setAnswerdCount] = useState(0);
    const [deadlineDatetime, setDeadlineDatetime] = useState(props.deadlineDatetime || "");
    const { t } = useTranslation();

    // MUIコンポーネントにsxプロップでスタイルを適用
    const flatCardSx = {
        backgroundColor: '#ffffff',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        mb: 2,
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative'
    };

    const headerBoxSx = {
        backgroundColor: '#90caf9',
        p: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const optionButtonSx = (isSelected, mode) => ({
        backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
        border: isSelected ? '2px solid #1976d2' : '2px solid #2196f3',
        color: isSelected ? '#1565c0' : '#2196f3',
        borderRadius: 2,
        p: 1.5,
        fontSize: '1rem',
        fontWeight: 500,
        textTransform: 'none',
        justifyContent: 'flex-start',
        boxShadow: 'none',
        height: '64px',
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        transformOrigin: 'center',
        willChange: 'transform, box-shadow, background-color',
        cursor: 'pointer',
        overflow: 'hidden',
        boxSizing: 'border-box',
        outline: 'none',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            transition: 'left 0.5s'
        },
        '&:hover::before': {
            left: '100%'
        },
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: isSelected ? '0 8px 25px rgba(25, 118, 210, 0.25)' : '0 8px 25px rgba(33, 150, 243, 0.2)',
            animationName: hoverPulse,
            animationDuration: '2s',
            animationTimingFunction: 'ease-in-out',
            animationIterationCount: 'infinite'
        }
    });

    const animationDelay = mode === "edit" ? 200 : 0;

    const stackItemSx = {
        position: 'relative',
        height: '64px',
        borderRadius: 2,
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        opacity: 0,
        animationName: fadeInScale,
        animationDuration: '0.6s',
        animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        animationDelay: `${animationDelay}ms`,
        animationFillMode: 'forwards',
        transformOrigin: 'center'
    };

    const progressSx = {
        height: 6,
        borderRadius: 3,
        backgroundColor: '#f0f0f0',
        overflow: 'hidden',
        '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            backgroundColor: '#2196f3'
        }
    };

    const statsBoxSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1,
        bgcolor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: 2,
        mt: 2
    };

    const deadlineBoxSx = {
        mt: 2,
        p: 2,
        bgcolor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: 2
    };

    return (
        <Card sx={flatCardSx}>
            <CardContent sx={{ p: 3 }}>
                {/* ヘッダー部分 */}
                <Fade in={true} timeout={800}>
                    <Box sx={{
                        ...headerBoxSx,
                        animationName: slideInFromLeft,
                        animationDuration: '0.6s',
                        animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        animationFillMode: 'forwards'
                    }}>
                        <QuestionMark sx={{ color: 'white', fontSize: '1.5rem', mr: 1 }} />
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {t("questionnaire.answer")}
                        </Typography>
                    </Box>
                </Fade>

                {/* 質問と選択肢 */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                        {props.question || "サンプル質問"}
                    </Typography>

                    <Stack spacing={2}>
                        {(props.options || ["選択肢1", "選択肢2", "選択肢3"]).map((option, i) => (
                            <Fade key={i} in={true} timeout={400 + i * 100}>
                                <Box sx={{
                                    ...stackItemSx,
                                    animationDelay: `${i * 100}ms`
                                }}>
                                    <Button
                                        sx={optionButtonSx(false, mode)}
                                        onClick={() => console.log('Option selected:', option)}
                                    >
                                        <RadioButtonUnchecked sx={{ mr: 1 }} />
                                        {option}
                                    </Button>
                                </Box>
                            </Fade>
                        ))}
                    </Stack>
                </Box>

                {/* 統計情報 */}
                <Collapse in={mode === "view"} timeout={600}>
                    <Box sx={{ 
                        ...statsBoxSx,
                        animationName: mode === "view" ? slideInFromLeft : 'none',
                        animationDuration: mode === "view" ? '0.6s' : '0s',
                        animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        animationDelay: '0.8s',
                        animationFillMode: 'both',
                        opacity: mode === "view" ? 1 : 0
                    }}>
                        <People sx={{ color: '#666666', mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#666666', fontWeight: 'bold' }}>
                            {t("questionnaire.answered")}: {answerdCount}
                        </Typography>
                    </Box>
                </Collapse>

                {/* 締切情報 */}
                <Collapse in={!!deadlineDatetime} timeout={400}>
                    <Box sx={{
                        ...deadlineBoxSx,
                        animationName: deadlineDatetime ? slideInFromLeft : 'none',
                        animationDuration: deadlineDatetime ? '0.5s' : '0s',
                        animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                        animationDelay: '0.2s',
                        animationFillMode: 'both'
                    }}>
                        <TextField
                            label={t("common.deadline")}
                            value={deadlineDatetime}
                            disabled
                            fullWidth
                            size="small"
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                        />
                    </Box>
                </Collapse>

                {/* プログレスバー */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                        回答進捗: 75%
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={75}
                        sx={progressSx}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default QuestionAnswerFixed;
